import * as React from "react";
import {
  Button,
  Callout,
  Dialog,
  FormGroup,
  InputGroup,
  OverlayToaster,
  Position,
  type OverlayToaster as OverlayToasterType,
} from "@blueprintjs/core";
import { css } from "@emotion/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ApiAdmin, {
  ApiError,
  type IGroup,
  type IInvitableGroup,
} from "../../../api/admin";
import { GroupMultiSelect } from "../../Admin/common/GroupMultiSelect";
import { INVITABLE_GROUPS_QUERY_KEY } from "./folderAdminQueryKeys";
import {
  dedupeGroupIdsByFolder,
  defaultContributorGroupId,
} from "./folderAdminInviteUserHelpers";
import {
  formatAllowedDomainList,
  INVITE_DOMAIN_NOT_ALLOWED_CODE,
  isEmailDomainAllowed,
  isValidEmailFormat,
} from "../../../../../smokepit/src/utils/inviteDomains";

interface FolderAdminInviteUserButtonProps {
  folder: { id: number; label: string };
}

function invitableGroupsToIGroups(groups: IInvitableGroup[]): IGroup[] {
  return groups.map((g) => ({
    id: g.id,
    name: g.name,
    user_ids: [],
    autogenerated: g.autogenerated,
    folder_label: g.folder_label,
    permission_label: g.permission_label,
  }));
}

export function FolderAdminInviteUserButton(
  props: FolderAdminInviteUserButtonProps
) {
  const { folder } = props;
  const [isOpen, setIsOpen] = React.useState(false);
  const [addErrors, setAddErrors] = React.useState<string[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = React.useState<number[]>([]);
  const toaster = React.useRef<OverlayToasterType>(null);
  const queryClient = useQueryClient();

  const invitableGroupsQuery = useQuery({
    queryKey: INVITABLE_GROUPS_QUERY_KEY,
    queryFn: () => ApiAdmin.getInvitableGroupsForFolderAdmin(),
    enabled: isOpen,
  });

  const invitableGroups = invitableGroupsQuery.data?.groups ?? [];
  const allowedDomains =
    invitableGroupsQuery.data?.allowed_invite_domains ?? [];
  const hasInitializedSelection = React.useRef(false);

  React.useEffect(() => {
    if (!isOpen) {
      hasInitializedSelection.current = false;
      return;
    }
    if (!hasInitializedSelection.current && invitableGroups.length > 0) {
      const defaultId = defaultContributorGroupId(invitableGroups, folder.id);
      setSelectedGroupIds(defaultId ? [defaultId] : []);
      hasInitializedSelection.current = true;
    }
  }, [isOpen, invitableGroups, folder.id]);

  const inviteMutation = useMutation({
    mutationFn: (data: { name: string; email: string; group_ids: number[] }) =>
      ApiAdmin.inviteUserForFolderAdmin(data),
    onSuccess: (_data, variables) => {
      toaster.current?.show({
        message: `Invitation sent to ${variables.email}`,
        intent: "success",
        timeout: 3000,
      });
      setAddErrors([]);
      setSelectedGroupIds([]);
      setIsOpen(false);
      queryClient.invalidateQueries({ queryKey: ["folderAdminOrgUsers"] });
      queryClient.invalidateQueries({
        queryKey: ["folderAdminFolderPermissions"],
      });
    },
  });

  const handleGroupChange = (_userId: number, groupIds: number[]) => {
    setSelectedGroupIds(dedupeGroupIdsByFolder(groupIds, invitableGroups));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const errors: string[] = [];
    const nameInput = event.currentTarget.elements.namedItem(
      "invite-user-name"
    ) as HTMLInputElement;
    const emailInput = event.currentTarget.elements.namedItem(
      "invite-user-email"
    ) as HTMLInputElement;

    const name = nameInput?.value;
    const email = emailInput?.value;

    if (!name || name.trim() === "") {
      errors.push("name");
    }
    if (!email || email.trim() === "") {
      errors.push("email");
    } else if (!isValidEmailFormat(email)) {
      errors.push("email (invalid format)");
    } else if (!isEmailDomainAllowed(email, allowedDomains)) {
      errors.push("email (domain not allowed)");
    }
    if (selectedGroupIds.length === 0) {
      errors.push("folder access");
    }

    if (errors.length > 0) {
      setAddErrors(errors);
      return;
    }

    inviteMutation.mutate({
      name: name.trim(),
      email: email.trim().toLowerCase(),
      group_ids: selectedGroupIds,
    });
  };

  const handleClose = () => {
    if (inviteMutation.isPending) return;
    setAddErrors([]);
    setSelectedGroupIds([]);
    inviteMutation.reset();
    setIsOpen(false);
  };

  const inviteError = inviteMutation.error;
  const isDuplicateInOrg =
    inviteError instanceof ApiError &&
    inviteError.code === "user_exists_in_org";
  const isDeactivatedInOrg =
    inviteError instanceof ApiError &&
    inviteError.code === "user_deleted_in_org";
  const isDomainNotAllowed =
    inviteError instanceof ApiError &&
    inviteError.code === INVITE_DOMAIN_NOT_ALLOWED_CODE;

  return (
    <>
      <OverlayToaster position={Position.BOTTOM_RIGHT} ref={toaster} />
      <Button
        icon="new-person"
        text="Invite User"
        intent="none"
        onClick={() => setIsOpen(true)}
      />
      <Dialog
        className="bp6-dark"
        css={css`
          width: 524px;
        `}
        title="Invite User"
        isOpen={isOpen}
        onClose={handleClose}
      >
        <form
          css={css`
            padding: 1rem;
          `}
          onSubmit={handleSubmit}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 1rem;
              margin-bottom: 1rem;
            `}
          >
            <FormGroup
              label="Name"
              labelFor="invite-user-name"
              labelInfo="(required)"
              intent={addErrors.includes("name") ? "danger" : "none"}
            >
              <InputGroup
                id="invite-user-name"
                intent={addErrors.includes("name") ? "danger" : "none"}
                disabled={inviteMutation.isPending}
                autoFocus
              />
            </FormGroup>

            <FormGroup
              label="Email"
              labelFor="invite-user-email"
              labelInfo="(required)"
              intent={
                addErrors.some((e) => e.startsWith("email")) ? "danger" : "none"
              }
              helperText={
                allowedDomains.length > 0
                  ? `Only ${formatAllowedDomainList(allowedDomains)} addresses can be invited under your Organization's rules. Contact an Organization Admin or FYBR for exceptions.`
                  : undefined
              }
            >
              <InputGroup
                id="invite-user-email"
                type="email"
                intent={
                  addErrors.some((e) => e.startsWith("email"))
                    ? "danger"
                    : "none"
                }
                disabled={inviteMutation.isPending}
              />
            </FormGroup>

            <FormGroup label="Folder Access" labelInfo="(required)">
              <GroupMultiSelect
                user={{
                  id: 0,
                  name: "",
                  email: "",
                  group_ids: selectedGroupIds,
                  organization_role: "member",
                }}
                groups={invitableGroupsToIGroups(invitableGroups)}
                onGroupChange={handleGroupChange}
                showOnlyFolderGroups
              />
            </FormGroup>
          </div>

          {inviteMutation.isError && inviteError && (
            <div style={{ marginBottom: "1rem" }}>
              <Callout
                intent={isDeactivatedInOrg ? "warning" : "danger"}
                title={
                  isDeactivatedInOrg
                    ? "Deactivated user found"
                    : isDuplicateInOrg
                      ? "User already exists"
                      : isDomainNotAllowed
                        ? "Email domain not allowed"
                        : "Could not invite user"
                }
              >
                <p>{inviteError.message}</p>
              </Callout>
            </div>
          )}

          <div
            css={css`
              display: flex;
              justify-content: space-between;
              align-items: center;
              width: 100%;
              padding-top: 0.5rem;
            `}
          >
            <div
              css={css`
                color: var(--danger);
                flex-grow: 1;
              `}
            >
              {addErrors.length > 0 &&
                `Missing or invalid fields: ${addErrors.join(", ")}.`}
            </div>
            <div
              css={css`
                display: flex;
                justify-content: end;
                gap: 1rem;
              `}
            >
              <Button
                text="Cancel"
                disabled={inviteMutation.isPending}
                intent="none"
                onClick={handleClose}
              />
              <Button
                text={inviteMutation.isPending ? "Sending..." : "Invite"}
                disabled={inviteMutation.isPending}
                intent="primary"
                type="submit"
              />
            </div>
          </div>
        </form>
      </Dialog>
    </>
  );
}
